import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, signal, viewChild } from '@angular/core';
import type * as THREE_NS from 'three';

type Three = typeof THREE_NS;

/** Monitored "services" on the globe surface: [latitude, longitude, healthy]. */
const NODES: [number, number, boolean][] = [
  [47.4, 8.5, true], // Zurich
  [51.5, -0.1, true],
  [40.7, -74.0, true],
  [37.8, -122.4, false],
  [35.7, 139.7, true],
  [1.35, 103.8, true],
  [-33.9, 151.2, true],
  [-23.5, -46.6, true],
  [19.1, 72.9, true],
];

/** Pairs of NODES linked by an arc with a travelling request "packet". */
const LINKS: [number, number][] = [
  [0, 1],
  [0, 2],
  [2, 3],
  [0, 4],
  [4, 5],
  [5, 6],
  [2, 7],
  [0, 8],
  [8, 5],
];

const RADIUS = 2;
const COLOR_A = 0x6c7ee4; // brand blue
const COLOR_B = 0xa78bfa; // violet
const COLOR_OK = 0x34d399;
const COLOR_WARN = 0xfbbf24;
const COLOR_PULSE = 0x67e8f9;

/**
 * The landing page's hero object: a slowly turning globe of points with monitored
 * nodes that emit status pulses, arcs carrying request "packets" between them, and a
 * heartbeat ring orbiting around it — PulseWatch watching services around the world.
 *
 * Three.js is imported lazily so it only ships to visitors of the landing page. The
 * render loop runs outside Angular, pauses while the globe is off-screen or the tab is
 * hidden, renders a single still frame for `prefers-reduced-motion`, and falls back to
 * a CSS orb when WebGL is unavailable.
 */
@Component({
  selector: 'app-pulse-globe',
  standalone: true,
  template: `
    <canvas #canvas aria-hidden="true" [class.is-ready]="ready()"></canvas>
    @if (!webgl()) {
      <div class="pg-fallback" aria-hidden="true"></div>
    }
  `,
  styleUrls: ['./pulse-globe.component.scss'],
})
export class PulseGlobeComponent implements AfterViewInit, OnDestroy {
  private readonly _canvas = viewChild.required<ElementRef<HTMLCanvasElement>>('canvas');

  /** Fades the canvas in once the first frame is drawn. */
  readonly ready = signal(false);
  readonly webgl = signal(true);

  private _cleanup: (() => void) | null = null;
  private _destroyed = false;

  constructor(
    private readonly _host: ElementRef<HTMLElement>,
    private readonly _zone: NgZone,
  ) {}

  ngAfterViewInit(): void {
    this._zone.runOutsideAngular(() => {
      import('three')
        .then((THREE) => {
          if (!this._destroyed) this._cleanup = this._start(THREE);
        })
        .catch(() => this.webgl.set(false));
    });
  }

  ngOnDestroy(): void {
    this._destroyed = true;
    this._cleanup?.();
  }

  private _start(THREE: Three): () => void {
    const canvas = this._canvas().nativeElement;
    let renderer: THREE_NS.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true, powerPreference: 'low-power' });
    } catch {
      this.webgl.set(false);
      return () => undefined;
    }
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setClearColor(0x000000, 0);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(38, 1, 0.1, 100);
    camera.position.set(0, 0, 8.2);

    const world = new THREE.Group();
    world.rotation.z = 0.18;
    scene.add(world);
    const globe = new THREE.Group();
    world.add(globe);

    const glow = this._glowTexture(THREE);
    const disposables: { dispose(): void }[] = [glow];
    const track = <T extends { dispose(): void }>(d: T): T => (disposables.push(d), d);

    // ── Core: a dark sphere that hides the far side, and a fresnel atmosphere ──
    globe.add(
      new THREE.Mesh(
        track(new THREE.SphereGeometry(RADIUS * 0.985, 64, 64)),
        track(new THREE.MeshBasicMaterial({ color: 0x0b1024, transparent: true, opacity: 0.92 })),
      ),
    );
    world.add(
      new THREE.Mesh(
        track(new THREE.SphereGeometry(RADIUS * 1.22, 64, 64)),
        track(
          new THREE.ShaderMaterial({
            uniforms: { uColor: { value: new THREE.Color(COLOR_A) } },
            vertexShader: `
              varying vec3 vNormal;
              void main() {
                vNormal = normalize(normalMatrix * normal);
                gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
              }`,
            fragmentShader: `
              uniform vec3 uColor;
              varying vec3 vNormal;
              void main() {
                float rim = pow(0.72 - dot(vNormal, vec3(0.0, 0.0, 1.0)), 3.0);
                gl_FragColor = vec4(uColor, 1.0) * rim;
              }`,
            side: THREE.BackSide,
            blending: THREE.AdditiveBlending,
            transparent: true,
            depthWrite: false,
          }),
        ),
      ),
    );

    // ── Surface: an even Fibonacci lattice of dots, tinted blue → violet ──
    const count = 2200;
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    const cA = new THREE.Color(COLOR_A);
    const cB = new THREE.Color(COLOR_B);
    const golden = Math.PI * (3 - Math.sqrt(5));
    for (let i = 0; i < count; i++) {
      const y = 1 - (i / (count - 1)) * 2;
      const r = Math.sqrt(1 - y * y);
      const theta = golden * i;
      positions.set([Math.cos(theta) * r * RADIUS, y * RADIUS, Math.sin(theta) * r * RADIUS], i * 3);
      const c = cA.clone().lerp(cB, (y + 1) / 2);
      colors.set([c.r, c.g, c.b], i * 3);
    }
    const dotsGeo = track(new THREE.BufferGeometry());
    dotsGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    dotsGeo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    globe.add(
      new THREE.Points(
        dotsGeo,
        track(
          new THREE.PointsMaterial({
            size: 0.055,
            map: glow,
            vertexColors: true,
            transparent: true,
            depthWrite: false,
            blending: THREE.AdditiveBlending,
          }),
        ),
      ),
    );

    // ── Nodes with expanding status pulses ──
    const toVec = (lat: number, lon: number, r = RADIUS) => {
      const phi = ((90 - lat) * Math.PI) / 180;
      const theta = ((lon + 180) * Math.PI) / 180;
      return new THREE.Vector3(-r * Math.sin(phi) * Math.cos(theta), r * Math.cos(phi), r * Math.sin(phi) * Math.sin(theta));
    };
    const ringGeo = track(new THREE.RingGeometry(0.07, 0.085, 48));
    const pulses: { mesh: THREE_NS.Mesh; phase: number }[] = [];
    const nodePositions = NODES.map(([lat, lon, ok], i) => {
      const pos = toVec(lat, lon);
      const color = ok ? COLOR_OK : COLOR_WARN;
      const dot = new THREE.Sprite(
        track(new THREE.SpriteMaterial({ map: glow, color, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending })),
      );
      dot.position.copy(pos.clone().multiplyScalar(1.005));
      dot.scale.setScalar(0.22);
      globe.add(dot);
      for (let k = 0; k < 2; k++) {
        const ring = new THREE.Mesh(
          ringGeo,
          track(new THREE.MeshBasicMaterial({ color, transparent: true, side: THREE.DoubleSide, depthWrite: false, blending: THREE.AdditiveBlending })),
        );
        ring.position.copy(pos.clone().multiplyScalar(1.01));
        ring.lookAt(pos.clone().multiplyScalar(2));
        globe.add(ring);
        pulses.push({ mesh: ring, phase: (i * 0.37 + k * 0.5) % 1 });
      }
      return pos;
    });

    // ── Arcs with travelling packets ──
    const packets: { sprite: THREE_NS.Sprite; curve: THREE_NS.QuadraticBezierCurve3; offset: number; speed: number }[] = [];
    const arcMat = track(new THREE.LineBasicMaterial({ color: COLOR_PULSE, transparent: true, opacity: 0.28, depthWrite: false, blending: THREE.AdditiveBlending }));
    const packetMat = track(new THREE.SpriteMaterial({ map: glow, color: COLOR_PULSE, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending }));
    LINKS.forEach(([a, b], i) => {
      const start = nodePositions[a];
      const end = nodePositions[b];
      const mid = start.clone().add(end).multiplyScalar(0.5);
      mid.setLength(RADIUS * (1.18 + start.distanceTo(end) * 0.16));
      const curve = new THREE.QuadraticBezierCurve3(start, mid, end);
      globe.add(new THREE.Line(track(new THREE.BufferGeometry().setFromPoints(curve.getPoints(64))), arcMat));
      const sprite = new THREE.Sprite(packetMat);
      sprite.scale.setScalar(0.16);
      globe.add(sprite);
      packets.push({ sprite, curve, offset: (i * 0.29) % 1, speed: 0.16 + (i % 3) * 0.05 });
    });

    // ── Heartbeat ring: an ECG trace wrapped around the globe ──
    const ecg = (x: number) => {
      // one beat per unit: flat, small P bump, sharp QRS spike, T wave
      const t = x - Math.floor(x);
      if (t < 0.36) return 0;
      if (t < 0.42) return 0.06 * Math.sin(((t - 0.36) / 0.06) * Math.PI);
      if (t < 0.46) return 0;
      if (t < 0.48) return -0.08 * ((t - 0.46) / 0.02);
      if (t < 0.5) return -0.08 + 0.58 * ((t - 0.48) / 0.02);
      if (t < 0.53) return 0.5 - 0.72 * ((t - 0.5) / 0.03);
      if (t < 0.55) return -0.22 + 0.22 * ((t - 0.53) / 0.02);
      if (t < 0.64) return 0;
      if (t < 0.74) return 0.1 * Math.sin(((t - 0.64) / 0.1) * Math.PI);
      return 0;
    };
    const beats = 3;
    const ecgPoints: THREE_NS.Vector3[] = [];
    for (let i = 0; i <= 720; i++) {
      const a = (i / 720) * Math.PI * 2;
      const r = RADIUS * 1.42 + ecg((i / 720) * beats) * 0.9;
      ecgPoints.push(new THREE.Vector3(Math.cos(a) * r, 0, Math.sin(a) * r));
    }
    const ecgGeo = track(new THREE.BufferGeometry().setFromPoints(ecgPoints));
    const ecgColors = new Float32Array(ecgPoints.length * 3);
    ecgGeo.setAttribute('color', new THREE.BufferAttribute(ecgColors, 3));
    const heartbeat = new THREE.Line(
      ecgGeo,
      track(new THREE.LineBasicMaterial({ vertexColors: true, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending })),
    );
    const heartbeatGroup = new THREE.Group();
    heartbeatGroup.rotation.x = 0.42;
    heartbeatGroup.add(heartbeat);
    world.add(heartbeatGroup);
    const pulseColor = new THREE.Color(COLOR_PULSE);
    const tailColor = new THREE.Color(COLOR_A);

    // A faint second orbit for depth.
    const orbit = new THREE.Mesh(
      track(new THREE.TorusGeometry(RADIUS * 1.62, 0.004, 8, 200)),
      track(new THREE.MeshBasicMaterial({ color: COLOR_B, transparent: true, opacity: 0.35, blending: THREE.AdditiveBlending, depthWrite: false })),
    );
    orbit.rotation.set(1.2, 0.3, 0);
    world.add(orbit);
    const satellite = new THREE.Sprite(packetMat);
    satellite.scale.setScalar(0.2);
    orbit.add(satellite);

    // ── Sizing, pointer parallax, visibility ──
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const host = this._host.nativeElement;
    const resize = () => {
      const { width, height } = host.getBoundingClientRect();
      if (!width || !height) return;
      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
    };
    const resizeObserver = new ResizeObserver(() => {
      resize();
      if (reducedMotion) frame(0);
    });
    resizeObserver.observe(host);
    resize();

    const pointer = { x: 0, y: 0 };
    const onPointer = (e: PointerEvent) => {
      pointer.x = (e.clientX / window.innerWidth) * 2 - 1;
      pointer.y = (e.clientY / window.innerHeight) * 2 - 1;
    };
    window.addEventListener('pointermove', onPointer, { passive: true });

    let visible = true;
    let raf = 0;
    let last = performance.now();
    let elapsed = 0;

    const frame = (dt: number) => {
      elapsed += dt;
      globe.rotation.y += dt * 0.12;
      world.rotation.x += (pointer.y * 0.18 - world.rotation.x) * 0.04;
      world.rotation.y += (pointer.x * 0.3 - world.rotation.y) * 0.04;

      for (const p of pulses) {
        const t = (elapsed * 0.45 + p.phase) % 1;
        p.mesh.scale.setScalar(1 + t * 4.5);
        (p.mesh.material as THREE_NS.MeshBasicMaterial).opacity = (1 - t) * 0.8;
      }
      for (const p of packets) {
        const t = (elapsed * p.speed + p.offset) % 1;
        p.sprite.position.copy(p.curve.getPoint(t));
        p.sprite.material.opacity = Math.sin(t * Math.PI);
      }

      // Light up a travelling segment of the heartbeat trace, like a monitor sweep.
      const head = (elapsed * 0.16) % 1;
      const n = ecgPoints.length;
      for (let i = 0; i < n; i++) {
        let d = head - i / n;
        if (d < 0) d += 1;
        const k = d < 0.35 ? Math.pow(1 - d / 0.35, 1.6) : 0;
        const c = tailColor.clone().multiplyScalar(0.18).lerp(pulseColor, k);
        ecgColors.set([c.r, c.g, c.b], i * 3);
      }
      ecgGeo.attributes['color'].needsUpdate = true;
      heartbeatGroup.rotation.y = elapsed * 0.05;

      satellite.position.set(Math.cos(elapsed * 0.5) * RADIUS * 1.62, Math.sin(elapsed * 0.5) * RADIUS * 1.62, 0);

      renderer.render(scene, camera);
      if (!this.ready()) this._zone.run(() => this.ready.set(true));
    };

    const loop = (now: number) => {
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;
      frame(dt);
      raf = requestAnimationFrame(loop);
    };
    const play = () => {
      if (reducedMotion || raf || !visible || document.hidden) return;
      last = performance.now();
      raf = requestAnimationFrame(loop);
    };
    const pause = () => {
      cancelAnimationFrame(raf);
      raf = 0;
    };

    const intersection = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible) play();
      else pause();
    });
    intersection.observe(host);
    const onVisibility = () => (document.hidden ? pause() : play());
    document.addEventListener('visibilitychange', onVisibility);

    // Reduced motion: one composed still frame, mid-animation so pulses are visible.
    frame(reducedMotion ? 1.4 : 0);
    play();

    return () => {
      pause();
      intersection.disconnect();
      resizeObserver.disconnect();
      window.removeEventListener('pointermove', onPointer);
      document.removeEventListener('visibilitychange', onVisibility);
      disposables.forEach((d) => d.dispose());
      renderer.dispose();
      renderer.forceContextLoss();
    };
  }

  /** Soft round sprite used for dots, nodes and packets. */
  private _glowTexture(THREE: Three): THREE_NS.Texture {
    const size = 64;
    const c = document.createElement('canvas');
    c.width = c.height = size;
    const ctx = c.getContext('2d')!;
    const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    g.addColorStop(0, 'rgba(255,255,255,1)');
    g.addColorStop(0.25, 'rgba(255,255,255,0.85)');
    g.addColorStop(0.6, 'rgba(255,255,255,0.18)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, size, size);
    const texture = new THREE.CanvasTexture(c);
    texture.colorSpace = THREE.SRGBColorSpace;
    return texture;
  }
}
